import { expectArray, expectBoolean, expectRecord, expectString, expectOptionalString, expectNullable } from "./schema";
export const NOTIFICATION_CATEGORIES = [
    "guardianLinkCompleted",
    "guardianLinkRequestRejected",
    "schedulePublished",
    "occurrenceChanged",
    "occurrenceCancelled",
    "replacementVideoPublished",
    "videoPublished",
    "eventReminder",
    "teachingAssignmentChanged",
    "todayOccurrenceChanged",
    "absenceRequestApproved",
    "absenceRequestRejected",
    "absenceRequestSubmitted",
    "scheduleValidationWarning",
    "schedulePublishRequired",
    "guardianLinkRequestSubmitted",
    "studentAbsenceReported",
    "studentAbsenceCancelled",
    "videoUploadFailed",
    "memberCommunicationPublished",
    "memberCommunicationUpdated"
];
export const NOTIFICATION_PRIORITIES = ["critical", "high", "normal", "low"];
function parseStringEnum(value, allowedValues, path) {
    const parsed = expectString(value, path);
    if (!allowedValues.includes(parsed)) {
        throw new Error(`${path} must be one of: ${allowedValues.join(", ")}`);
    }
    return parsed;
}
function hasOwn(record, key) {
    return Object.prototype.hasOwnProperty.call(record, key);
}
function parseNotificationPayloadValue(value, path) {
    if (value === null) {
        return null;
    }
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
        return value;
    }
    throw new Error(`${path} must be a string, number, boolean, or null`);
}
export function parseNotificationCategory(value, path = "notificationCategory") {
    return parseStringEnum(value, NOTIFICATION_CATEGORIES, path);
}
export function parseNotificationPriority(value, path = "notificationPriority") {
    return parseStringEnum(value, NOTIFICATION_PRIORITIES, path);
}
export function parseNotificationInboxItem(value, path = "notificationInboxItem") {
    const record = expectRecord(value, path);
    return {
        itemId: expectString(record.itemId, `${path}.itemId`),
        notificationEventId: expectString(record.notificationEventId, `${path}.notificationEventId`),
        category: parseNotificationCategory(record.category, `${path}.category`),
        priority: parseNotificationPriority(record.priority, `${path}.priority`),
        title: expectString(record.title, `${path}.title`),
        body: expectString(record.body, `${path}.body`),
        targetType: expectOptionalString(record.targetType, `${path}.targetType`),
        targetId: expectOptionalString(record.targetId, `${path}.targetId`),
        targetDisplayLabel: expectOptionalString(record.targetDisplayLabel, `${path}.targetDisplayLabel`),
        relatedStudentIds: expectArray(record.relatedStudentIds, expectString, `${path}.relatedStudentIds`),
        readAt: expectOptionalString(record.readAt, `${path}.readAt`),
        archivedAt: expectOptionalString(record.archivedAt, `${path}.archivedAt`),
        createdAt: expectString(record.createdAt, `${path}.createdAt`)
    };
}
export function parseNotificationEventRecord(value, path = "notificationEventRecord") {
    const record = expectRecord(value, path);
    const payloadRecord = expectRecord(record.payload, `${path}.payload`);
    const payload = Object.fromEntries(Object.entries(payloadRecord).map(([key, itemValue]) => [key, parseNotificationPayloadValue(itemValue, `${path}.payload.${key}`)]));
    return {
        eventId: expectString(record.eventId, `${path}.eventId`),
        category: parseNotificationCategory(record.category, `${path}.category`),
        priority: parseNotificationPriority(record.priority, `${path}.priority`),
        sourceType: expectString(record.sourceType, `${path}.sourceType`),
        sourceId: expectString(record.sourceId, `${path}.sourceId`),
        triggeredByPlatformUserId: expectOptionalString(record.triggeredByPlatformUserId, `${path}.triggeredByPlatformUserId`),
        occurredAt: expectString(record.occurredAt, `${path}.occurredAt`),
        payload,
        dedupeKey: expectOptionalString(record.dedupeKey, `${path}.dedupeKey`),
        status: expectString(record.status, `${path}.status`)
    };
}
export function parseNotificationDeliveryRecord(value, path = "notificationDeliveryRecord") {
    const record = expectRecord(value, path);
    return {
        deliveryId: expectString(record.deliveryId, `${path}.deliveryId`),
        notificationEventId: expectString(record.notificationEventId, `${path}.notificationEventId`),
        platformUserId: expectString(record.platformUserId, `${path}.platformUserId`),
        channel: expectString(record.channel, `${path}.channel`),
        status: expectString(record.status, `${path}.status`),
        providerMessageId: expectOptionalString(record.providerMessageId, `${path}.providerMessageId`),
        errorCode: expectOptionalString(record.errorCode, `${path}.errorCode`),
        createdAt: expectString(record.createdAt, `${path}.createdAt`),
        sentAt: expectOptionalString(record.sentAt, `${path}.sentAt`)
    };
}
export function parseNotificationPrefs(value, path = "notificationPrefs") {
    const record = expectRecord(value, path);
    return {
        platformUserId: expectString(record.platformUserId, `${path}.platformUserId`),
        inAppEnabled: expectBoolean(record.inAppEnabled, `${path}.inAppEnabled`),
        emailEnabled: expectBoolean(record.emailEnabled, `${path}.emailEnabled`),
        criticalEmailEnabled: expectBoolean(record.criticalEmailEnabled, `${path}.criticalEmailEnabled`),
        highEmailEnabled: expectBoolean(record.highEmailEnabled, `${path}.highEmailEnabled`),
        serviceMessageEnabled: expectBoolean(record.serviceMessageEnabled, `${path}.serviceMessageEnabled`),
        digestEnabled: expectBoolean(record.digestEnabled, `${path}.digestEnabled`),
        quietHours: expectOptionalString(record.quietHours, `${path}.quietHours`),
        updatedAt: expectString(record.updatedAt, `${path}.updatedAt`)
    };
}
export function parseUpdateNotificationPrefsRequest(value, path = "updateNotificationPrefsRequest") {
    const record = expectRecord(value, path);
    const parsed = {};
    if (hasOwn(record, "inAppEnabled")) {
        parsed.inAppEnabled = expectBoolean(record.inAppEnabled, `${path}.inAppEnabled`);
    }
    if (hasOwn(record, "emailEnabled")) {
        parsed.emailEnabled = expectBoolean(record.emailEnabled, `${path}.emailEnabled`);
    }
    if (hasOwn(record, "criticalEmailEnabled")) {
        parsed.criticalEmailEnabled = expectBoolean(record.criticalEmailEnabled, `${path}.criticalEmailEnabled`);
    }
    if (hasOwn(record, "highEmailEnabled")) {
        parsed.highEmailEnabled = expectBoolean(record.highEmailEnabled, `${path}.highEmailEnabled`);
    }
    if (hasOwn(record, "serviceMessageEnabled")) {
        parsed.serviceMessageEnabled = expectBoolean(record.serviceMessageEnabled, `${path}.serviceMessageEnabled`);
    }
    if (hasOwn(record, "digestEnabled")) {
        parsed.digestEnabled = expectBoolean(record.digestEnabled, `${path}.digestEnabled`);
    }
    if (hasOwn(record, "quietHours")) {
        parsed.quietHours = expectOptionalString(record.quietHours, `${path}.quietHours`);
    }
    return parsed;
}
export function parseNotificationsResponse(value, path = "notificationsResponse") {
    const record = expectRecord(value, path);
    return {
        items: expectArray(record.items, parseNotificationInboxItem, `${path}.items`),
        prefs: expectNullable(record.prefs, parseNotificationPrefs, `${path}.prefs`)
    };
}
